import React from 'react';
import { Box, LinearProgress, Paper, Typography } from '@mui/material';
import LevelAvatar, { LEVEL_COLORS } from '../LevelAvatar/LevelAvatar';

/**
 * The employee's own points and status.
 *
 * Deliberately forward-looking: it shows what comes next and never comments on
 * being slow. One encouraging line at a time, written by the API.
 */
const MyPoints = ({ status, profileImage, name, email }) => {
    if (!status || status.enabled === false) {
        return null;
    }

    const level = status.level;
    const color = level ? LEVEL_COLORS[level] : null;
    const label = level ? level.charAt(0).toUpperCase() + level.slice(1) : null;

    // Progress through the current milestone band, for the bar under the message
    const step = Math.max(1, status.milestone?.step ?? 100);
    const pointsAway = status.milestone?.points_away ?? step;
    const milestonePct = Math.min(100, Math.max(0, Math.round(((step - pointsAway) / step) * 100)));

    return (
        <Paper
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 3 }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LevelAvatar
                    level={level}
                    src={profileImage}
                    name={name}
                    email={email}
                    size={56}
                    showTooltip={false}
                />
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                        {name || email}
                    </Typography>
                    {label ? (
                        <Typography variant="body2" sx={{ color, fontWeight: 700 }}>
                            {label}
                        </Typography>
                    ) : (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            No status yet
                        </Typography>
                    )}
                    {status.has_status && (
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', display: 'block' }}
                        >
                            Average {status.average.toFixed(2)} points over your last{' '}
                            {status.window_count} posts
                        </Typography>
                    )}
                </Box>
            </Box>

            {status.message && (
                <Box sx={{ mt: 2, backgroundColor: '#eff6ff', borderRadius: 1.5, px: 1.75, py: 1.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {status.message}
                    </Typography>
                    {status.has_status && (
                        <LinearProgress
                            variant="determinate"
                            value={milestonePct}
                            sx={{
                                mt: 1,
                                height: 5,
                                borderRadius: 99,
                                backgroundColor: 'rgba(37,99,235,0.18)',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 99,
                                    backgroundColor: '#2563eb',
                                },
                            }}
                        />
                    )}
                </Box>
            )}

            <Box
                sx={{
                    mt: 2,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 1,
                    textAlign: 'center',
                }}
            >
                {[
                    { label: 'This month', value: status.month_points },
                    { label: 'Total points', value: status.total_points },
                    { label: 'Posts read', value: status.total_posts },
                ].map((item) => (
                    <Box key={item.label}>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                        >
                            {item.value}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {item.label}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Paper>
    );
};

export default MyPoints;
